import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { useLogger } from '~/composables/useLogger';
import { isClient } from '~/utils';

/**
 * Everything about navigating to an agent profile and back out of it.
 *
 * Kept in one file on purpose: the two directions are one feature and share the
 * same slug resolution, so the flag that hides it, the placeholder slug and the
 * back behaviour are all readable without jumping between modules.
 *
 * - `useAgentProfileLink` — the four entry points (PDP contact section and
 *   form, Shorts feed, home testimonials)
 * - `useAgentProfileNavigation` — the back button inside the profile hero
 */

/* -------------------------------------------------------------------------- */
/* Slug and route                                                             */
/* -------------------------------------------------------------------------- */

/** Base path of the public agent profile page (`app/pages/corretor/[slug].vue`). */
export const AGENT_PROFILE_BASE_PATH = '/corretor';

/**
 * Anything carrying an agent's identity across the app.
 *
 * Structural on purpose: the PDP passes an `Agent`, the Shorts feed passes its
 * own reduced agent object and the home testimonials pass a hand-filled slug.
 * All of them resolve through the same function so there is a single place to
 * change.
 */
export interface AgentProfileLinkSource {
	id?: string | null;
	slug?: string | null;
}

/**
 * SINGLE SOURCE OF TRUTH for the agent slug used by every entry point.
 *
 * The slug comes from the API and from nowhere else — never rebuilt from the
 * agent's name. A derived slug misses the disambiguation suffix real ones carry
 * (`juliana-marques-1`), so it would send the user to the profile page's fatal
 * 404, and these are SSR links: every PDP and the home page would ship real
 * `<a href>`s pointing at it.
 *
 * Returning `null` instead makes the feature light up on its own: each entry
 * point renders plain text until the API sends `slug` inside the embedded
 * `agent` object, and becomes a link the moment it does.
 *
 * @param source - Agent-shaped object
 * @returns The slug, or `null` when the API has not sent one
 */
export function resolveAgentProfileSlug(
	source: AgentProfileLinkSource | null | undefined
): string | null {
	return source?.slug?.trim() || null;
}

/**
 * Builds the in-app path to an agent profile.
 *
 * Returns `null` — rather than a broken path — when the slug cannot be
 * resolved, so callers can render plain text instead of a dead link.
 *
 * @param source - Agent-shaped object
 * @returns `/corretor/{slug}`, or `null` when there is no slug
 */
export function buildAgentProfileHref(
	source: AgentProfileLinkSource | null | undefined
): string | null {
	const slug = resolveAgentProfileSlug(source);
	if (!slug) return null;

	return `${AGENT_PROFILE_BASE_PATH}/${encodeURIComponent(slug)}`;
}

/* -------------------------------------------------------------------------- */
/* Origin storage                                                             */
/* -------------------------------------------------------------------------- */

export const AGENT_PROFILE_ENTRY_URL_STORAGE_KEY = 'agentProfileEntryUrl';

const logger = useLogger('agentProfileEntry');

interface AgentProfileEntry {
	url: string;
	/**
	 * Agent the user was heading to, by id.
	 *
	 * By id and not by slug because slug is text: the one embedded in the
	 * property and the one the profile endpoint returns can differ in accent,
	 * case or escaping and the comparison would fail silently, sending the user
	 * to the home page instead of back where they came from. Ids are opaque.
	 *
	 * `null` where the origin has no agent behind it — the home testimonials —
	 * and the entry is then accepted without checking.
	 */
	agentId: string | null;
	createdAt: number | null;
}
const AGENT_PROFILE_ENTRY_MAX_AGE_MS = 30 * 60 * 1000;

function readWriteSafely<T>(
	operation: 'read' | 'persist' | 'clear',
	fallback: T,
	run: () => T
): T {
	if (!isClient()) return fallback;

	try {
		return run();
	} catch (error) {
		logger.error('Agent profile entry URL sessionStorage operation failed', {
			operation,
			storage_key: AGENT_PROFILE_ENTRY_URL_STORAGE_KEY,
			error
		});

		return fallback;
	}
}

function setAgentProfileEntry(entry: AgentProfileEntry): void {
	if (!entry.url) return;

	readWriteSafely('persist', undefined, () =>
		sessionStorage.setItem(
			AGENT_PROFILE_ENTRY_URL_STORAGE_KEY,
			JSON.stringify(entry)
		)
	);
}

function getAgentProfileEntry(): AgentProfileEntry | null {
	return readWriteSafely('read', null, () => {
		const raw = sessionStorage.getItem(AGENT_PROFILE_ENTRY_URL_STORAGE_KEY);
		if (!raw) return null;

		const parsed = JSON.parse(raw) as Partial<AgentProfileEntry> | null;
		if (!parsed || typeof parsed.url !== 'string' || !parsed.url) return null;

		const createdAt =
			typeof parsed.createdAt === 'number' ? parsed.createdAt : null;

		if (
			createdAt === null ||
			Date.now() - createdAt > AGENT_PROFILE_ENTRY_MAX_AGE_MS
		) {
			clearAgentProfileEntry();
			return null;
		}

		return {
			url: parsed.url,
			agentId: typeof parsed.agentId === 'string' ? parsed.agentId : null,
			createdAt
		};
	});
}

export function clearAgentProfileEntry(): void {
	readWriteSafely('clear', undefined, () =>
		sessionStorage.removeItem(AGENT_PROFILE_ENTRY_URL_STORAGE_KEY)
	);
}

/**
 * Records where the user is leaving from, right before opening an agent profile.
 *
 * Reads the URL from `window.location` rather than `route.fullPath` **on
 * purpose**: the Shorts feed rewrites its own URL with a raw
 * `history.replaceState` (see `useShortsRouteSync`), which the Vue router never
 * observes — `route.fullPath` there still points at the short the user *entered*
 * on, not the one they are watching. `window.location` is the only source that
 * is right for all three origins, and it keeps the PDP's own `entrySource`
 * query intact so its back button keeps working too.
 *
 * @param agent - The agent being opened, used to scope the entry to that profile
 * @param pathOverride - Path to record instead of the current one, without query
 */
export function captureAgentProfileEntry(
	agent: AgentProfileLinkSource | null | undefined,
	pathOverride?: string | null
): void {
	if (!import.meta.client) return;

	const path = pathOverride?.trim() || window.location.pathname;

	setAgentProfileEntry({
		url: `${path}${window.location.search}`,
		agentId: agent?.id?.trim() || null,
		createdAt: Date.now()
	});
}

/**
 * Single entry point for every "click the agent's name or logo" spot: the PDP
 * contact section and form, the Shorts feed and the home testimonials.
 *
 * Pair the two returns on the same element — the href makes it a real link
 * (middle-click, open in new tab, crawlable), the click handler records where
 * to send the user back to:
 *
 * ```vue
 * <NuxtLink v-if="agentProfileHref" :to="agentProfileHref" @click="handleAgentProfileClick">
 * ```
 *
 * `agentProfileHref` is `null` whenever the link must not exist — the
 * `ft_agent_profile_entry` flag is off, or there is no slug to build one from —
 * and every caller already renders plain text in that case. That is what makes
 * this the single kill switch for the whole feature: turning the flag off
 * restores each origin page to exactly what it did before, including the Shorts
 * feed, whose agent block goes back to opening the contact form.
 *
 * The profile page itself stays reachable by direct URL, and its back button
 * keeps working: with no entry recorded it falls back to the home page.
 *
 * @param source - Agent-shaped object
 */
export function useAgentProfileLink(
	source: MaybeRefOrGetter<AgentProfileLinkSource | null | undefined>,
	originPath?: MaybeRefOrGetter<string | null | undefined>
) {
	const { ft_agent_profile_entry: isEntryPointEnabled } = useFeatureFlags();

	const agentProfileHref = computed(() =>
		isEntryPointEnabled ? buildAgentProfileHref(toValue(source)) : null
	);

	function handleAgentProfileClick(): void {
		if (!agentProfileHref.value) return;

		captureAgentProfileEntry(toValue(source), toValue(originPath));
	}

	const agentProfileLinkProps = computed(() => ({
		profileHref: agentProfileHref.value,
		onClick: handleAgentProfileClick
	}));

	return {
		agentProfileHref,
		handleAgentProfileClick,
		agentProfileLinkProps
	};
}

export const AGENT_PROFILE_FALLBACK_BACK_URL = '/';

interface UseAgentProfileNavigationOptions {
	profileId?: MaybeRefOrGetter<string | null | undefined>;
}

export function useAgentProfileNavigation(
	options: UseAgentProfileNavigationOptions = {}
) {
	const router = useRouter();

	function resolveDestinationUrl(): string {
		const entry = getAgentProfileEntry();
		if (!entry) return AGENT_PROFILE_FALLBACK_BACK_URL;

		const currentId = toValue(options.profileId)?.trim();

		if (currentId && entry.agentId && entry.agentId !== currentId) {
			clearAgentProfileEntry();
			return AGENT_PROFILE_FALLBACK_BACK_URL;
		}

		return entry.url;
	}

	async function handleBackButtonClick() {
		const destinationUrl = resolveDestinationUrl();

		try {
			await router.push(destinationUrl);
			clearAgentProfileEntry();
		} catch {
			return;
		}
	}

	return {
		handleBackButtonClick
	};
}
